import { useHydrateAtoms } from "jotai/utils";
import {
  Account,
  BuiltInApp,
  Session,
  ZSL,
  accountAtom,
  canvasAtom,
  chatbotsAppAtom,
  currentAccountIdAtom,
  edgesAtom,
  interfacesAppAtom,
  isEmbedAtom,
  isPublicAtom,
  nodesAtom,
  sessionAtom,
  showCanvasTemplateWidgetAtom,
  tablesAppAtom,
  zslAtom,
  Toggles,
  togglesAtom,
  showAIStreamCreateModalAtom,
  isAiPageAtom,
  isViewOnlyDowntimeOverrideAtom,
  RawCanvas,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
} from "@zapier/canvas-state";
import { aiPromptAtom, showWelcomeModalAtom } from "@zapier/streaming";
import { useCurrentAccountId } from "@zapier/identity";
import { convertCanvasSchemaForReactFlow } from "../../utils/convertCanvasSchemaForReactFlow";

export function CanvasHydrator({
  canvas: rawCanvas,
  children,
  session,
  account,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  toggles,
  isPublic = false,
  isEmbed = false,
  isAiPage = false,
  showAIStreamCreateModal = false,
  prompt = "",
  isTemplatePage = false,
}: {
  canvas: RawCanvas;
  children: React.ReactNode;
  session?: Session;
  account?: Account;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  toggles: Toggles;
  isPublic?: boolean;
  isEmbed?: boolean;
  isAiPage?: boolean;
  isTemplatePage?: boolean;
  showAIStreamCreateModal?: boolean;
  prompt?: string;
}) {
  const isViewer = rawCanvas.access_level === "can_view"; // This will be true when is_public is true (so true for public links)

  const isViewOnlyMode =
    isViewer ||
    isPublic ||
    isTemplatePage ||
    isEmbed ||
    toggles.viewOnlyDowntime;

  const canvas = convertCanvasSchemaForReactFlow({ isViewOnlyMode, rawCanvas });

  const showWelcomeModal = determineShowWelcomeModal({
    isAiPage,
    isViewOnlyMode,
    rawCanvas,
  });

  const currentAccountId = useCurrentAccountId();

  useHydrateAtoms([
    [canvasAtom, canvas],
    [nodesAtom, canvas.nodes],
    [edgesAtom, canvas.edges],
    [currentAccountIdAtom, currentAccountId],
    [accountAtom, account || null],
    [sessionAtom, session || null],
    [zslAtom, template],
    [tablesAppAtom, tablesApp],
    [interfacesAppAtom, interfacesApp],
    [chatbotsAppAtom, chatbotsApp],
    [togglesAtom, toggles],
    [isPublicAtom, isPublic],
    [isEmbedAtom, isEmbed],
    [isAiPageAtom, isAiPage],
    [isTemplatePageAtom, isTemplatePage],
    [showCanvasTemplateWidgetAtom, false],
    [showWelcomeModalAtom, showWelcomeModal],
    [showAIStreamCreateModalAtom, showAIStreamCreateModal],
    [aiPromptAtom, prompt],
    [isViewOnlyDowntimeOverrideAtom, toggles.viewOnlyDowntime],
    [isViewOnlyModeAtom, isViewOnlyMode],
  ]);

  return children;
}

function determineShowWelcomeModal({
  isAiPage,
  isViewOnlyMode,
  rawCanvas,
}: {
  isAiPage: boolean;
  isViewOnlyMode: boolean;
  rawCanvas: RawCanvas;
}) {
  const { nodes } = rawCanvas;
  if (isAiPage) {
    return false;
  }
  if (isViewOnlyMode) {
    return false;
  }

  return (
    nodes.length < 2 &&
    nodes.every((node) => node.node_type === "default") &&
    nodes.every((node) => node.label === "") &&
    nodes.every((node) => !node.services || node.services.length === 0)
  );
}
